/** @module Camunda/ProcessView */
define([
	"dojo/_base/declare",
	"underscore",
	"csi",
	"csi/html",
	"csi/locale",
	"Camunda",
	"./_BpmnWidget",
	"dojo/text!csi/templates/htmlWithDetail.html"
], function(declare, _, csi, html, locale, Camunda, _BpmnWidget, template) {

	const DETAIL_VIEW_ID = "Detail";
	const PROCESS_ACTIVITY_VIEW_ID = "ProcessActivity";

	return declare(_BpmnWidget, {
		templateString: template,
		pluginsRight: ["ProcessLegend"],

		/** @property {string} detailLabel - label for detail view */
		detailLabel: "",

		// attach points
		htmlDetailNode: null,
		htmlDetailHeaderNode: null,
		htmlDetailScrollNode: null,
		htmlDetailTitleNode: null,

		/**
		 * @override
		 * Always create a side panel
		 */
		_postViewDeferred: function(ctx, dfds) {
			dfds.push(this._createSidePanel(null));
		},

		/**
		 * @override
		 * Show task details or process activity based on selection
		 */
		onClickElement: function(taskId) {
			this._createSidePanel(taskId);
		},

		/**
		 * Create task details panel if the task with given id is found.
		 * Create process activity panel otherwise
		 *
		 * @param {string} taskId
		 */
		_createSidePanel: function(taskId) {
			const cache = this.cacheView;
			const process = this.getViewNode();
			const processDefinition = cache.get(process.ProcessDefinition);
			const taskDefinitions = cache.getNodes(processDefinition.Tasks);
			var taskDefinition = taskId ? _.findWhere(taskDefinitions, {Id: taskId}) : null;

			if (taskDefinition && !Camunda.taskDefinitionIsUserTask(taskDefinition)) {
				taskDefinition = null;
			}

			this._destroyChildView(DETAIL_VIEW_ID);
			this._destroyChildView(PROCESS_ACTIVITY_VIEW_ID);

			let result;
			if (taskDefinition) {
				// look for running/completed task; otherwise, use task definition
				const tasks = cache.getNodes(process.Tasks);
				const tasksForDefinition = _.where(tasks, {TaskDefinition: taskDefinition.$URL});
				const ordered = csi.sort(tasksForDefinition, "Iteration");
				const task = ordered.last();

				// show task detail
				this.set("detailLabel", locale.resource("Detail"));
				result = this._insertAndCreateView(DETAIL_VIEW_ID, task || taskDefinition, this.htmlDetailScrollNode, {
					hideEmptyValues: true
				});
			} else {
				this.set("detailLabel", locale.resource("ProcessActivity"));
				result = this._insertAndCreateView(PROCESS_ACTIVITY_VIEW_ID, process, this.htmlDetailScrollNode);
			}

			return result;
		},

		/**
		 * Destroy the child view by its Id
		 * @param {string} viewId
		 */
		_destroyChildView: function(viewId) {
			const view = this.getChildView(viewId);
			if (view) {
				view.destroyView();
			}
		},

		/**
		 * @override
		 * Add CSS for tasks to indicate their status
		 */
		onViewEnd: function() {
			const cache = this.cacheView;
			const process = this.getViewNode();
			const tasks = cache.getNodes(process.Tasks);

			// map from task definition id to last executed task
			const taskDefinitionIdToTaskMap = csi.hashArray(tasks, (task) => {
				const taskDefinition = cache.getNode(task.TaskDefinition);
				return taskDefinition ? taskDefinition.Id : "";
			});

			// iterate over each element in the diagram and add some CSS classes to user and service tasks
			const elementRegistry = this._bpmn.get('elementRegistry');
			elementRegistry.forEach((element, gfx) => {
				const elementType = element.type;

				const isUserTask = elementType == this.USER_TASK;
				if (elementType != this.SERVICE_TASK && !isUserTask) {
					return;
				}

				// it is Id of task in process definition
				const taskDefinitionId = element.id;

				// find the executed task and mark process elements according to the state of executed task
				const task = taskDefinitionIdToTaskMap[taskDefinitionId];
				if (task) {
					const state = task.ActivityState;
					let cssClass = "csi-camunda-other-task";
					switch (state) {
						case Camunda.STATE_ERROR:
							cssClass = this.CSS_ERROR_TASK;
							break;
						case Camunda.STATE_STARTED:
							cssClass = this.CSS_RUNNING_TASK;
							break;
						case Camunda.STATE_SUSPENDED:
							cssClass = this.CSS_SUSPENDED_TASK;
							break;
						case Camunda.STATE_COMPLETED:
							cssClass = this.CSS_COMPLETED_TASK;
							break;
						case Camunda.STATE_CANCELLED:
							cssClass = this.CSS_CANCELLED_TASK;
							// create svg background pattern definition for cancelled task
							this._addDef('<pattern id="csi-pattern-not-applicable" x="0" y="0" width="5" height="5" patternUnits="userSpaceOnUse"><path d="M 0 5 L 0 5 L 5 0" stroke="#dadada" stroke-width="1"></path></pattern>');
							break;
					}
					this._addTaskCSS(taskDefinitionId, cssClass);
				} else {
					this._addTaskCSS(taskDefinitionId, this.CSS_NOT_STARTED_TASK);
				}

				this._addTaskCSS(taskDefinitionId, isUserTask ? this.CSS_USER_TASK : this.CSS_SERVICE_TASK);
			});
		},

		/**
		 * @override
		 * Return revision for current process
		 * @return {string}
		 */
		_getProcessDefinitionRevisionUrl: function() {
			return this.getViewNode().ProcessDefinition;
		},

		/**
		 * Apply label to title node
		 */
		_setDetailLabelAttr: {node: "htmlDetailTitleNode", type: "innerText"},

		/**
		 * @override
		 * Highlight process activity items belonging to this element
		 */
		onHoverElement: function(elementId) {
			const view = this.getChildView(PROCESS_ACTIVITY_VIEW_ID);
			if (view) {
				view.onHoverElement(elementId);
			}
		},

		/**
		 * @override
		 * Remove highlighting of process activity items belonging to this element
		 */
		onOutElement: function(elementId) {
			const view = this.getChildView(PROCESS_ACTIVITY_VIEW_ID);
			if (view) {
				view.onOutElement(elementId);
			}
		},

	});
});